import { createSlice } from '@reduxjs/toolkit';
import { attendanceApi } from '../../services/attendanceApi';

const initialState = {
  attendance: [],
  currentSession: null,
  loading: false,
  error: null,
  filters: {
    date: '',
    status: '',
    search: '',
  },
  pagination: {
    currentPage: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  stats: {
    presentToday: 0,
    absentToday: 0,
    lateToday: 0,
    avgHoursToday: '0h',
  },
};

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    setAttendance: (state, action) => {
      state.attendance = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        date: '',
        status: '',
        search: '',
      };
      state.pagination.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setStats: (state, action) => {
      state.stats = action.payload;
    },
    setCurrentSession: (state, action) => {
      state.currentSession = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        attendanceApi.endpoints.getAttendance.matchFulfilled,
        (state, action) => {
          state.attendance = action.payload.data || action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addMatcher(
        attendanceApi.endpoints.getAttendance.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        attendanceApi.endpoints.getAttendance.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error.message || 'Failed to fetch attendance data';
        }
      )
      .addMatcher(
        attendanceApi.endpoints.getAttendanceHistory.matchFulfilled,
        (state, action) => {
          const { total, page, limit } = action.payload.pagination || {};
          state.pagination.total = total || 0;
          state.pagination.totalPages = Math.ceil((total || 0) / (limit || 10));
          state.pagination.currentPage = page || 1;
        }
      )
      .addMatcher(
        attendanceApi.endpoints.clockIn.matchFulfilled,
        (state, action) => {
          state.currentSession = action.payload;
        }
      )
      .addMatcher(
        attendanceApi.endpoints.clockOut.matchFulfilled,
        (state) => {
          state.currentSession = null;
        }
      );
  },
});

export const {
  setAttendance,
  setFilters,
  clearFilters,
  setCurrentPage,
  setPagination,
  setStats,
  setCurrentSession,
} = attendanceSlice.actions;

// Selectors
export const selectAttendanceFilters = (state) => state.attendance.filters;
export const selectAttendancePagination = (state) => state.attendance.pagination;
export const selectAttendanceStats = (state) => state.attendance.stats;
export const selectCurrentSession = (state) => state.attendance.currentSession;

// Thunks for API integration
export const fetchAttendance = (params) => async (dispatch) => {
  try {
    const result = await dispatch(
      attendanceApi.endpoints.getAttendanceHistory.initiate(params)
    ).unwrap();

    dispatch(setPagination({
      currentPage: result.pagination?.page || 1,
      total: result.pagination?.total || 0,
      totalPages: Math.ceil((result.pagination?.total || 0) / (result.pagination?.limit || 10)),
    }));

    return result;
  } catch (error) {
    console.error('Error fetching attendance:', error);
    throw error;
  }
};

export const clockIn = (data = {}) => async (dispatch) => {
  try {
    const result = await dispatch(
      attendanceApi.endpoints.clockIn.initiate(data)
    ).unwrap();

    dispatch(setCurrentSession(result));
    return result;
  } catch (error) {
    console.error('Error clocking in:', error);
    throw error;
  }
};

export const clockOut = (data = {}) => async (dispatch) => {
  try {
    const result = await dispatch(
      attendanceApi.endpoints.clockOut.initiate(data)
    ).unwrap();

    dispatch(setCurrentSession(null));
    return result;
  } catch (error) {
    console.error('Error clocking out:', error);
    throw error;
  }
};

export default attendanceSlice.reducer;
